import {Dictionary} from '@malt/types-utils';
import {DefaultErrorHandler} from './errorHandler';
import {defaultHttpClient} from './defaultHttpClient';
import {ErrorHandler, HttpRequest, HttpService, HttpServiceResponse} from './types';
import {httpLogger, HttpLoggerArgs} from './logger';

export interface HttpClient {
    get(url: string, params?: Record<string, unknown>): Promise<any>;
    post(url: string, params?: Record<string, unknown>): Promise<any>;
    put(url: string, params?: Record<string, unknown>): Promise<any>;
    delete(url: string, params?: Record<string, unknown>): Promise<any>;
    [propName: string]: any;
}

interface HttpClientParams {
    client: HttpClient;
    errorHandler: ErrorHandler;
}

export class DefaultHttpService implements HttpService {
    private client: HttpClient;
    private errorHandler: ErrorHandler;

    constructor(
        {client, errorHandler}: HttpClientParams = {
            client: defaultHttpClient,
            errorHandler: new DefaultErrorHandler(),
        },
    ) {
        this.client = client;
        this.errorHandler = errorHandler;
    }

    async get<T>(url: string, params?: any, logger = true): Promise<HttpServiceResponse<T>> {
        const operation = () => {
            return this.client.get(url, {
                params,
                paramsSerializer: (_params: Dictionary<any>) => new URLSearchParams(_params).toString(),
            });
        };

        return this.httpOperationHandler(operation, logger ? {url, params, method: 'GET'} : undefined);
    }

    async post<T>(url: string, params?: any, logger = true): Promise<HttpServiceResponse<T>> {
        const operation = () => this.client.post(url, params);

        return this.httpOperationHandler(operation, logger ? {url, params, method: 'POST'} : undefined);
    }

    async put<T>(url: string, params?: any, logger = true): Promise<HttpServiceResponse<T>> {
        const operation = () => this.client.put(url, params);

        return this.httpOperationHandler(operation, logger ? {url, params, method: 'PUT'} : undefined);
    }

    async delete<T>(url: string, params?: any, logger = true): Promise<HttpServiceResponse<T>> {
        const operation = () => this.client.delete(url, {data: params});

        return this.httpOperationHandler<T>(operation, logger ? {url, params, method: 'DELETE'} : undefined);
    }

    async request<T>(request: HttpRequest, logger?: boolean): Promise<HttpServiceResponse<T>> {
        switch (request.method) {
            case 'GET':
                return this.get(request.url, request.params, logger);
            case 'POST':
                return this.post(request.url, request.params, logger);
            case 'PUT':
                return this.put(request.url, request.params, logger);
            case 'DELETE':
                return this.delete(request.url, request.params, logger);
            case 'HEAD':
            case 'PATCH':
            case 'OPTIONS':
            case 'TRACE':
                throw Error(`Unhandled method ${request.method}`);
            default:
                throw Error(`Unhandled method ${request.method}`);
        }
    }

    private async httpOperationHandler<T>(operation: () => Promise<any>, data?: HttpLoggerArgs): Promise<HttpServiceResponse<T>> {
        let response;
        let error: any = null;

        try {
            if (process.env.NODE_ENV === 'development' && data) {
                response = await httpLogger(operation, data);
            } else {
                response = await operation();
            }
        } catch (err) {
            this.errorHandler.handle(err);
            error = err;
            response = {};
        }

        return [response.data, error, response];
    }
}

export const httpService = new DefaultHttpService();
